import { useContext, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import Modal from "react-responsive-modal"

import { ReportModal } from "./ReportModal"
import VicinityContext from "../context/vicinityContext"
import { FLAGS_API_URL } from "../const/paths"
import { useLocationUpdate } from "../hooks/useLocationUpdate"
import { usePlaceSelect } from "../hooks/usePlaceSelect"
import { useAuth } from "../hooks/useAuth"
import { searchPlaces } from "../services/placesConnector"
import "./SearchBox.css"
import { LOCALSTORAGE_COUNTRYCODE, LOCALSTORAGE_PLACE } from "../const/storage"
import { ToastNoPlace } from "./ToastNotification"

import googleLogo from "../images/google_on_white_hdpi.png"

const SEARCH_PROMPT = "Where would you like to explore?"

// The place's name, or the default prompt for a place without one (such
// as the app's initial world-map placeholder), never "undefined".
const placeholderFor = (place) =>
    place?.fromGoogle?.name || place?.name || SEARCH_PROMPT

export default function SearchBox() {
    let vicinityData = useContext(VicinityContext)
    let place = useContext(VicinityContext).place
    let setPlace = useContext(VicinityContext).setPlace
    let countryCode = useContext(VicinityContext).countryCode
    let setCountryCode = useContext(VicinityContext).setCountryCode
    const { canDetectLocation } = useContext(VicinityContext)

    const handleLocationUpdate = useLocationUpdate()
    const isLoggedIn = useAuth()
    const {
        handlePlaceSelect,
        searchInput,
        setSearchInput,
        suggestions,
        setSuggestions,
    } = usePlaceSelect()

    const [openAddReport, setOpenAddReport] = useState(false)
    const onOpenReportModal = () => setOpenAddReport(true)
    const onCloseReportModal = () => setOpenAddReport(false)

    useEffect(() => {
        let placeData =
            JSON.parse(localStorage.getItem(LOCALSTORAGE_PLACE)) || null

        if (placeData) {
            setPlace(placeData)
            const searchInputBox = document.getElementById("searchInputBox")
            if (searchInputBox) {
                searchInputBox.placeholder = placeholderFor(placeData)
            }
            setCountryCode(placeData.country_code)
            localStorage.setItem(
                LOCALSTORAGE_COUNTRYCODE,
                JSON.stringify(placeData.country_code)
            )
        }
    }, [setPlace, setCountryCode])

    // Also re-run on sign-in, when the input first appears.
    useEffect(() => {
        const searchInputBox = document.getElementById("searchInputBox")
        if (searchInputBox) {
            searchInputBox.placeholder = placeholderFor(place)
        }
    }, [place, isLoggedIn])

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (searchInput.length > 2) {
                const results = await searchPlaces(searchInput)
                setSuggestions(results)
            } else {
                setSuggestions([])
            }
        }

        const timeoutId = setTimeout(fetchSuggestions, 300)
        return () => clearTimeout(timeoutId)
    }, [searchInput, setSuggestions])

    // Search, detect-location and report all need an account, so a
    // signed-out visitor gets none of them. The component stays mounted for
    // the saved-place restore above and the toast.
    if (!isLoggedIn) return <ToastNoPlace />

    return (
        <div className="search-container">
            <ToastNoPlace />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "8px",
                }}
            >
                {vicinityData.countryCode !== "GLOBAL" && (
                    <>
                        {place?.id && place?.slug ? (
                            <Link
                                to={`/analysis/${place.slug}`}
                                style={{ textDecoration: "none" }}
                            >
                                <img
                                    src={`${FLAGS_API_URL}/${place?.country_code}.svg`}
                                    data-bs-toggle="tooltip"
                                    data-bs-title={
                                        "Active country: " + place?.country
                                    }
                                    alt={
                                        "Active country: " + place?.country_code
                                    }
                                    style={{
                                        width: "32px",
                                        height: "auto",
                                        marginRight: "8px",
                                        cursor: "pointer",
                                    }}
                                />
                            </Link>
                        ) : (
                            <img
                                src={`${FLAGS_API_URL}/${countryCode}.svg`}
                                data-bs-toggle="tooltip"
                                data-bs-title={
                                    "Active country: " + place?.country
                                }
                                alt={"Active country: " + countryCode}
                                style={{
                                    width: "32px",
                                    height: "auto",
                                    marginRight: "8px",
                                }}
                            />
                        )}
                    </>
                )}

                {vicinityData.countryCode === "GLOBAL" && (
                    <i
                        className="bi bi-globe"
                        style={{
                            width: "32px",
                            height: "auto",
                            marginRight: "8px",
                        }}
                    ></i>
                )}
            </div>
            <div className="dropdown w-75">
                <input
                    id="searchInputBox"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    type="search"
                    className="form-control"
                    placeholder={SEARCH_PROMPT}
                    aria-label="Search"
                    aria-controls="suggestions-list"
                    role="combobox"
                    aria-expanded={suggestions.length > 0}
                    autoComplete="off"
                />
                {suggestions.length > 0 && (
                    <ul id="searchSuggestions" className="dropdown-menu show">
                        {suggestions.map((suggestion) => (
                            <li key={suggestion.place_id}>
                                <button
                                    className="dropdown-item"
                                    type="button"
                                    onClick={() =>
                                        handlePlaceSelect(suggestion.place_id)
                                    }
                                >
                                    <i className="bi bi-geo-alt me-2"></i>
                                    {suggestion.description}
                                </button>
                            </li>
                        ))}
                        <span className="pe-2" style={{ float: "right" }}>
                            <img
                                src={googleLogo}
                                alt="Powered by Google"
                                title="Powered by Google"
                            />
                        </span>
                    </ul>
                )}
            </div>
            <div
                className={`searchbar-icons${
                    canDetectLocation ? "" : " searchbar-icons-disabled"
                }`}
            >
                {/* Keyed so the tooltip is rebuilt with the new title. */}
                <i
                    key={canDetectLocation ? "enabled" : "disabled"}
                    id="search-icon"
                    className="bi bi-crosshair ps-3 text-secondary"
                    role="button"
                    aria-disabled={!canDetectLocation}
                    data-bs-toggle="tooltip"
                    data-bs-title={
                        canDetectLocation
                            ? "Detect my current location"
                            : "Location access is off. Turn it on in Settings."
                    }
                    onClick={canDetectLocation ? handleLocationUpdate : undefined}
                ></i>
            </div>
            <div className="searchbar-icons">
                <i
                    id="contribute-report"
                    className="bi bi-plus-circle ps-3"
                    data-bs-toggle="tooltip"
                    data-bs-title="Contribute a report"
                    onClick={onOpenReportModal}
                ></i>
                <Modal open={openAddReport} onClose={onCloseReportModal} center>
                    <ReportModal
                        onClose={onCloseReportModal}
                        reportPlace={place}
                    />
                </Modal>
            </div>
        </div>
    )
}
